/* eslint-disable react/prop-types */
import React, { useState } from "react";
import { Add } from "@material-ui/icons";
import {
  Dialog,
  DialogTitle,
  FormControl,
  Button,
  makeStyles,
  DialogActions,
  DialogContent,
  FormHelperText,
  TextField,
  CircularProgress,
  Snackbar
} from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";
import useFormFields from "lib/forms";
import trimFormFields from "lib/forms_utils";
import { createCompany } from "lib/services/companies";

const useStyles = makeStyles(theme => ({
  addCompanyButton: {
    backgroundColor: "#90cb7c"
  },
  button: {
    margin: theme.spacing(1)
  },
  companySelectHelperText: {
    color: "#e86861",
    fontSize: "large",
    fontWeight: "bold"
  },
  shortnameHelperText: {
    fontSize: "smaller",
    color: "rgb(84, 84, 85)"
  },
  wrapper: {
    margin: theme.spacing(1),
    position: "relative"
  },
  buttonProgress: {
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12
  }
}));

function Alert(props) {
  // eslint-disable-next-line react/jsx-props-no-spreading
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

export default function AddCompanyComponent({ onSuccess }) {
  const classes = useStyles(() => ({
    dialogContent: {
      padding: "20px"
    }
  }));
  const [open, setOpen] = useState(false);
  const [fields, handleFieldChange, resetFields] = useFormFields({
    name: "",
    short_name: ""
  });
  const [loading, setLoading] = useState(false);
  const [snackbarState, setSnackbarState] = useState({
    open: false,
    severity: "",
    message: ""
  });

  const handleDialogOpen = () => {
    setOpen(true);
  };

  const handleDialogClose = () => {
    setOpen(false);
    resetFields();
  };

  const handleFormSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    const newSnackbarState = {
      open: true,
      severity: "success",
      message: "Company created successfully!"
    };
    try {
      await createCompany({ ...trimFormFields(fields) });
      handleDialogClose();
      if (onSuccess && typeof onSuccess === "function") onSuccess();
    } catch (error) {
      if (error.response && error.response.data.title === "Conflict") {
        newSnackbarState.severity = "warning";
        newSnackbarState.message =
          "You're late! The company short name is already in use. Please enter a different one";
      } else {
        newSnackbarState.severity = "error";
        newSnackbarState.message =
          "An error occurred. Please try again shortly";
      }
    } finally {
      setLoading(false);
      setSnackbarState(newSnackbarState);
    }
  };

  const handleSnackbarClose = (_event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setSnackbarState(prevState => {
      return {
        ...prevState,
        open: false
      };
    });
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        size="large"
        className={classes.button}
        startIcon={<Add />}
        onClick={handleDialogOpen}
      >
        New Company
      </Button>
      <Dialog
        className={classes.dialogContent}
        open={open}
        aria-labelledby="add-company-form-dialog-title"
      >
        <DialogTitle id="add-company-form-dialog-title">
          Create new company
        </DialogTitle>
        <form onSubmit={handleFormSubmit}>
          <DialogContent>
            <FormControl fullWidth required className={classes.formControl}>
              <TextField
                label="Full name"
                name="name"
                value={fields.name}
                onChange={handleFieldChange}
                inputProps={{
                  pattern: "[\\wÀ-ú']+(\\s?[\\wÀ-ú'+])*?"
                }}
                required
                fullWidth
              />
              <TextField
                label="Shortname"
                name="short_name"
                value={fields.short_name}
                onChange={handleFieldChange}
                inputProps={{
                  pattern: "[a-z]+(_?[a-z+])*?"
                }}
                required
                fullWidth
              />
              <FormHelperText className={classes.shortnameHelperText}>
                Case insensitive. Can only contain alphanumeric characters
                and/or underscores
              </FormHelperText>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose}>Cancel</Button>
            <div className={classes.wrapper}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
              >
                Create Company
              </Button>
              {loading && (
                <CircularProgress
                  size={24}
                  className={classes.buttonProgress}
                />
              )}
            </div>
          </DialogActions>
        </form>
      </Dialog>
      <Snackbar
        open={snackbarState.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarState.severity}>
          {snackbarState.message}
        </Alert>
      </Snackbar>
    </>
  );
}
